import { Query, QueryClient, useQueryClient } from 'react-query';
import { Updater } from 'react-query/types/core/utils';

/**
 * For react-query.
 * This hooks implements a way of optimistic update.
 * Returns a function to perform an update in cached queries.
 *
 * The function needs a key, a selector (to find the proper queries to change), and an updater function.
 *
 * This example shows how to filter a deleted trip
 * @example
 * const update = useOptimisticUpdates();
 * update<Trip[]>('trips', (query) => query.state.data.id === 123456, (trips) => trips.filter(trip => trip.id !== 123456))
 *
 * */
export function useOptimisticUpdates() {
    const queryClient = useQueryClient();

    return async <T>(
        key: string,
        selector: (query: Query<unknown, unknown, T>) => boolean,
        updater: Updater<T | undefined, T>,
    ) => {
        const queries = searchQueries<T>(queryClient, key, selector);

        // Cancel ongoing queries to prevent the optimistic update from being overrided
        await Promise.all(queries.map((query) => queryClient.cancelQueries(query.queryKey)));

        // Updating data
        await Promise.all(
            queries.map((query) => {
                queryClient.setQueryData(query.queryKey, updater);
                return queryClient.invalidateQueries(query.queryKey);
            }),
        );
    };
}

function searchQueries<T>(
    queryClient: QueryClient,
    key: string,
    selector: (query: Query<unknown, unknown, T>) => boolean,
) {
    return queryClient
        .getQueryCache()
        .getAll()
        .filter((query: Query) => {
            if (query.queryKey[0] !== key) {
                return false;
            }

            return selector(query as Query<unknown, unknown, T>);
        });
}
